import { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { Reveal } from "@/components/site/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Loader2, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const embedSrc = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vp = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\?h=([\w]+))?/);
  if (vp) return `https://player.vimeo.com/video/${vp[1]}${vp[2] ? `?h=${vp[2]}&` : "?"}title=0&portrait=0&byline=0&dnt=1`;
  const vm = url.match(/vimeo\.com\/(\d+)(?:\/([\w]+))?/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}${vm[2] ? `?h=${vm[2]}&` : "?"}title=0&portrait=0&byline=0&dnt=1`;
  return null;
};

export const VideoFrame = ({ video }) => {
  const src = embedSrc(video.video_url);
  return (
    <div className="relative aspect-video rounded-[24px] overflow-hidden border border-white/15 bg-black">
      {src ? (
        <iframe src={src} title={video.title} className="absolute inset-0 w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen loading="lazy" />
      ) : (
        <video src={video.video_url} poster={video.poster || undefined} controls preload={video.poster ? "none" : "metadata"} playsInline className="absolute inset-0 w-full h-full object-contain" />
      )}
    </div>
  );
};

export default function Testimonianze() {
  usePageMeta(
    "Recensioni e testimonianze impianti dentali: video dei pazienti | Dentista Sostenibile",
    "Recensioni vere e video testimonianze dei pazienti che hanno fatto gli impianti dentali a carico immediato: esperienze con All on 4, impianti zigomatici e denti fissi in 6 ore. Opinioni reali, 5,0 su Google."
  );
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(({ data }) => setVideos(data)).catch(() => setVideos([]));
  }, []);

  return (
    <main className="bg-[#15201A] min-h-screen">
      <Header />
      <section className="pt-header pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#C98E6E] flex items-center gap-2">
              <PlayCircle size={18} /> Recensioni e video testimonianze
            </p>
            <h1 className="mt-4 font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white" data-testid="testimonianze-h1">
              Testimonianze impianti dentali: i nostri pazienti, <em className="italic text-[#7D9285]">con la loro voce</em>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-white/60 max-w-2xl">
              Recensioni vere ed esperienze di chi ha fatto gli impianti dentali a carico immediato — All on 4, impianti zigomatici,
              denti fissi in 6 ore — raccontate direttamente dai pazienti. Nessun attore: solo opinioni reali di chi è tornato a sorridere.
            </p>
          </Reveal>
          {videos === null ? (
            <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-white/60" size={36} /></div>
          ) : videos.length === 0 ? (
            <p className="py-24 text-white/60" data-testid="testimonianze-empty">Nessun video disponibile al momento.</p>
          ) : (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonianze-grid">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.06}>
                  <figure data-testid={`testimonianza-video-${v.id}`}>
                    <VideoFrame video={v} />
                    <figcaption className="mt-4 text-white/80 font-semibold text-base">{v.title}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
      <WhatsAppButton />
    </main>
  );
}
